/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useRef, useState } from 'react';
import {
  Stack,
  Link,
  Spinner,
  bindControlToForm,
  autoSaved,
} from '@piwikpro/ui-components';
import classNameBind from 'classnames/bind';
import styles from './VerificationCodeInput.module.css';

const TWO_FACTOR_INPUT_NUMBER_OF_FIELDS = 6;

const bindClasses = classNameBind.bind(styles);

export interface IVerificationCodeInputProps {
  name: string
  value: string
  disabled: boolean
  onBackupCodesLinkClick?: () => void
  backupCodesLinkText?: string
  withRightSideSpinner?: boolean
  onChange: ({
    target: { name, value },
  }: {
    target: { name: string, value: string }
  }) => void
}

export const VerificationCodeInput = ({
  name,
  onChange,
  value,
  disabled,
  onBackupCodesLinkClick,
  backupCodesLinkText,
  withRightSideSpinner = true,
}: IVerificationCodeInputProps) => {
  const [currentField, setCurrentField] = useState(0);
  const inputsRefs = useRef<HTMLElement[]>([]);

  const handleClick = () => {
    if (inputsRefs.current[currentField]) {
      inputsRefs.current[currentField].focus();
    } else {
      inputsRefs.current[currentField - 1].focus();
    }
  };

  const handleChange = (newValue: string) => {
    onChange({
      target: {
        name,
        value: newValue,
      },
    });
  };

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      const key = Number(e.keyCode || e.charCode);
      if (key === 69) { // disallow enter number in "e" notation
        e.preventDefault();
        return;
      }
      if (key === 8 || key === 46) { // handle backspace
        if (currentField !== 0) {
          handleChange(value.substring(0, value.length - 1));
        }
      }
    },
    [currentField],
  );

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedValue = event.clipboardData.getData('text/plain').trim().replace(/\D+/g, '');
    const calculatedPosition = currentField + pastedValue.length;

    setTimeout(() => {
      setCurrentField(
        calculatedPosition > TWO_FACTOR_INPUT_NUMBER_OF_FIELDS
          ? TWO_FACTOR_INPUT_NUMBER_OF_FIELDS : calculatedPosition,
      );
    }, 0);
  };

  useEffect(() => {
    inputsRefs.current[currentField]?.focus();
  }, [currentField]);

  useEffect(() => {
    setCurrentField(value.length);
  }, [value]);

  return (
    <div className={bindClasses('wrapper')}>
      <Stack vertical horizontalAlignment="middle">
        <Stack.Item>
          <div
            role="button"
            tabIndex={-1}
            className={bindClasses('inputs')}
            onClick={handleClick}
          >
            {Array.from(Array(TWO_FACTOR_INPUT_NUMBER_OF_FIELDS).keys()).map(id => (
              <input
                ref={(el) => {
                  inputsRefs.current[id] = el!;
                }}
                key={id}
                onKeyDown={handleKeyDown}
                min={0}
                max={9}
                maxLength={1}
                name={name}
                value={value[id] || ''}
                onPaste={handlePaste}
                onChange={(e) => {
                  const slicedValue = e.target.value.slice(0, TWO_FACTOR_INPUT_NUMBER_OF_FIELDS);
                  if (currentField < TWO_FACTOR_INPUT_NUMBER_OF_FIELDS) {
                    if (e.target.value !== '') {
                      setCurrentField(currentField + 1);
                      handleChange(value + slicedValue);
                    }
                  }
                }}
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                className={bindClasses(
                  { filled: value[id], disabled },
                  'single-input',
                )}
                disabled={disabled}
              />
            ))}
            {withRightSideSpinner && disabled && (
              <div className={bindClasses('spinner')}>
                <Spinner className="small" />
              </div>
            )}
          </div>
        </Stack.Item>
        {backupCodesLinkText && (
          <Stack.Item>
            <div
              className={bindClasses('link', { disabled })}
            >
              <Link
                size="small"
                onClick={onBackupCodesLinkClick}
              >
                {backupCodesLinkText}
              </Link>
            </div>
          </Stack.Item>
        )}
      </Stack>
    </div>
  );
};

export const VerificationCodeInputControl = bindControlToForm()(VerificationCodeInput);
export const AutoSavedVerificationCodeInputControl = autoSaved({ saveOnChange: true })(
  VerificationCodeInputControl,
);
